// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { getOrganizationUsers } from '@/account/api/get-organization-users';
import {
  FullAuditFilter,
  getFullAuditEvents,
} from '@/audit/api/get-full-audit-events';
import { FullAuditTable } from '@/audit/components/full-audit-table';
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { PageContent } from '@/components/page-content';
import { SupportErrorBoundary } from '@/components/support-error-boundary';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { DateTime } from 'luxon';
import { z } from 'zod';

const PAGE_SIZE = 100;

const searchSchema = z.object({
  page: z.number().int().positive().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  referenceRelation: z.array(z.string()).optional(),
  sourceType: z.array(z.string()).optional(),
  actorId: z.array(z.string()).optional(),
});

export const Route = createFileRoute('/_app/audit')({
  component: AuditRoute,
  validateSearch: searchSchema,
});

/**
 * Full audit page component that displays all audit events across the organization.
 * This includes events from all objects (assets, products, projects, etc).
 * Defaults to showing the last 7 days of audit events with server-side pagination.
 */
function AuditRoute() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { user } = useCurrentUser();

  const page = search.page ?? 1;

  const defaultStartDate = DateTime.now().minus({ days: 7 });
  const defaultEndDate = DateTime.now();

  const startDate = search.startDate
    ? DateTime.fromISO(search.startDate)
    : defaultStartDate;
  const endDate = search.endDate
    ? DateTime.fromISO(search.endDate)
    : defaultEndDate;

  const referenceRelations = search.referenceRelation;
  const sourceTypes = search.sourceType;
  const actorIds = search.actorId;

  const { data: orgUsersData } = useQuery({
    queryKey: ['organizationUsers', user.organization.key],
    queryFn: () => getOrganizationUsers(user.organization.key),
  });

  const actorOptions = (orgUsersData?.users ?? []).map((orgUser) => ({
    id: orgUser.id,
    label: `${orgUser.firstName} ${orgUser.lastName}`,
  }));

  // Build the filter for the API call
  const filter: FullAuditFilter = {
    pageSize: PAGE_SIZE,
    pageNumber: page,
    startDate:
      (search.startDate ? startDate : defaultStartDate).toISODate() ??
      undefined,
    endDate:
      (search.endDate ? endDate : defaultEndDate).toISODate() ?? undefined,
    referenceRelations,
    sourceTypes,
    actorIds,
  };

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['fullAudit', filter],
    queryFn: () => getFullAuditEvents(filter),
  });

  const handleStartDateChange = (date: DateTime | null) => {
    navigate({
      search: {
        ...search,
        startDate: date?.toISODate() ?? undefined,
        page: undefined,
      },
    });
  };

  const handleEndDateChange = (date: DateTime | null) => {
    navigate({
      search: {
        ...search,
        endDate: date?.toISODate() ?? undefined,
        page: undefined,
      },
    });
  };

  const handleReferenceRelationChange = (relations: string[]) => {
    navigate({
      search: {
        ...search,
        referenceRelation: relations.length > 0 ? relations : undefined,
        page: undefined,
      },
    });
  };

  const handleSourceTypeChange = (types: string[]) => {
    navigate({
      search: {
        ...search,
        sourceType: types.length > 0 ? types : undefined,
        page: undefined,
      },
    });
  };

  const handleActorChange = (ids: string[]) => {
    navigate({
      search: {
        ...search,
        actorId: ids.length > 0 ? ids : undefined,
        page: undefined,
      },
    });
  };

  const handlePageChange = (newPage: number) => {
    navigate({
      search: { ...search, page: newPage },
    });
  };

  return (
    <PageContent>
      <SupportErrorBoundary message="An error occurred displaying audit events. Please try refreshing the page.">
        <FullAuditTable
          events={data?.events ?? []}
          totalCount={data?.totalCount ?? null}
          page={page}
          pageSize={PAGE_SIZE}
          isLoading={isLoading}
          isFetching={isFetching}
          startDate={startDate}
          endDate={endDate}
          referenceRelations={referenceRelations ?? []}
          sourceTypes={sourceTypes ?? []}
          actorOptions={actorOptions}
          actorIds={actorIds ?? []}
          onStartDateChange={handleStartDateChange}
          onEndDateChange={handleEndDateChange}
          onReferenceRelationChange={handleReferenceRelationChange}
          onSourceTypeChange={handleSourceTypeChange}
          onActorChange={handleActorChange}
          onPageChange={handlePageChange}
        />
      </SupportErrorBoundary>
    </PageContent>
  );
}
